import React, { useEffect, useState } from "react";
import { Hospital, Eye } from "lucide-react";
import DocumentViewer from "../../components/DocumentViewer";
import { API_HOSPITAL_LIST, API_GET_EMPLOYEE_FILE } from "../../config/Endpoints";
import { getApi } from "../../utils/api";
import { formatCalendarDateDisplay } from "../../utils/helpers";
import { getAuthToken } from "../../utils/Storage";
import styles from "./profile-insurance-details.module.css";
import axios from "axios";

function displayDate(value) {
  return value ? formatCalendarDateDisplay(value) : "-";
}

function displayGender(value) {
  const normalized = String(value ?? "").trim().toUpperCase();
  const genderLabels = {
    M: "Male",
    MALE: "Male",
    F: "Female",
    FEMALE: "Female",
    O: "Others",
    OTHER: "Others",
    OTHERS: "Others",
  };
  return genderLabels[normalized] || value || "-";
}

const ProfileInsuranceDetails = ({ insuranceDetails }) => {
  const [hospitalsByCity, setHospitalsByCity] = useState({});
  const [hospitalsLoading, setHospitalsLoading] = useState(true);
  const [hospitalsError, setHospitalsError] = useState("");
  const [loadingDocument, setLoadingDocument] = useState(null);
  const [viewerUrl, setViewerUrl] = useState(null);
  const [viewerType, setViewerType] = useState("application/octet-stream");
  const [viewerName, setViewerName] = useState("Insurance document");

  const employeeInsurance = insuranceDetails?.employeeInsurance ?? {};
  const familyInsurance = insuranceDetails?.familyInsurance ?? {};



  useEffect(() => {
    let active = true;
    setHospitalsLoading(true);
    setHospitalsError("");

    getApi(API_HOSPITAL_LIST, getAuthToken("accessToken"))
      .then((response) => {
        if (active) {
          setHospitalsByCity(response?.data?.data ?? response?.data ?? {});
        }
      })
      .catch(() => {
        if (active) setHospitalsError("Unable to load network hospitals.");
      })
      .finally(() => {
        if (active) setHospitalsLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);


  const openDocument = async (path) => {
    setLoadingDocument(path);

    try {
      const response = await axios.get(API_GET_EMPLOYEE_FILE, {
        headers: {
          Authorization: `Bearer ${getAuthToken("accessToken")}`,
        },
        params: {
          filepath: path,
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: response.data.type || "image/png",
      });

      const objectUrl = URL.createObjectURL(blob);

      setViewerUrl((current) => {
        if (current) {
          URL.revokeObjectURL(current);
        }

        return objectUrl;
      });

      setViewerType(blob.type || "image/png");

      setViewerName(
        path.split(/[\\/]/).filter(Boolean).pop() || "Insurance document",
      );
    } catch (error) {
      console.error("Failed to load insurance document:", error);
    } finally {
      setLoadingDocument(null);
    }
  };

  useEffect(() => {
    if (!viewerUrl) return;

    const openModalButton = document.getElementById(
      "open-profile-insurance-document-view"
    );

    if (openModalButton) {
      openModalButton.click();
    }
  }, [viewerUrl]);

  const cards = [
    {
      title: "Self",
      data: employeeInsurance,
    },
    ...(
      (
        familyInsurance.spouseName ||
        familyInsurance.spouseDob ||
        familyInsurance.spouseInsuranceNumber ||
        familyInsurance.spouseInsuranceFrom ||
        familyInsurance.spouseInsuranceTo ||
        familyInsurance.spouseInsuranceSoftCopy
      )
      ? [
        {
          title: "Spouse",
          data: {
            name: familyInsurance.spouseName,
            dob: familyInsurance.spouseDob,
            insuranceNumber: familyInsurance.spouseInsuranceNumber,
            insuranceFrom: familyInsurance.spouseInsuranceFrom,
            insuranceTo: familyInsurance.spouseInsuranceTo,
            insuranceSoftCopy: familyInsurance.spouseInsuranceSoftCopy,
          },
        },
      ]
      : []),
    ...(
      (
        familyInsurance.childName ||
        familyInsurance.childDob ||
        familyInsurance.childInsuranceNumber ||
        familyInsurance.childInsuranceFrom ||
        familyInsurance.childInsuranceTo ||
        familyInsurance.childInsuranceSoftCopy
      )
      ? [
        {
          title: "Child",
          data: {
            name: familyInsurance.childName,
            dob: familyInsurance.childDob,
            gender: familyInsurance.childGender,
            insuranceNumber: familyInsurance.childInsuranceNumber,
            insuranceFrom: familyInsurance.childInsuranceFrom,
            insuranceTo: familyInsurance.childInsuranceTo,
            insuranceSoftCopy: familyInsurance.childInsuranceSoftCopy,
          },
        },
      ]
      : []),
  ];

  return (
    <div className={styles.page}>
      <section className={styles.hospitalSection}>
        <div className={styles.hospitalCopy}>
          <span className={styles.hospitalIcon}><Hospital size={20} /></span>
          <div>
            <h3 className="heading-xs">Network Hospitals</h3>
            <p>View hospitals covered under this insurance plan.</p>
          </div>
        </div>
        <div className={`dropdown ${styles.hospitalDropdown}`}>
          <button className={`${styles.hospitalTrigger} dropdown-toggle`} type="button" data-bs-toggle="dropdown">
            View covered hospitals
          </button>
          <ul className={`${styles.hospitalMenu} dropdown-menu dropdown-menu-end`}>
            <li className={styles.menuTitle}>Hospitals covered under this plan</li>
            {hospitalsLoading ? <li className={styles.hospitalMessage}>Loading hospitals...</li> : null}
            {hospitalsError ? <li className={styles.hospitalMessage}>{hospitalsError}</li> : null}
            {!hospitalsLoading && !hospitalsError
              ? Object.entries(hospitalsByCity).map(([city, hospitals]) => (
                <li className={styles.cityGroup} key={city}>
                  <h4>{city}</h4>
                  <ul>
                    {(Array.isArray(hospitals) ? hospitals : []).map((hospital) => (
                      <li key={`${city}-${hospital}`} className={styles.hospitalItem}>
                        <Hospital size={16} />
                        <span>{hospital}</span>
                      </li>
                    ))}
                  </ul>
                </li>
              ))
              : null}
          </ul>
        </div>
      </section>

     <div className={styles.insuranceCards}>
  {cards.map(({ title, data: card }) => (
            <section className={styles.insuranceCard} key={title}>
              <p className="heading-xs text-accent mb-0">{title}</p>

              <dl>
                {title !== "Self" ? (
                  <div>
                    <dt>{title} Name</dt>
                    <dd>{card?.name || "-"}</dd>
                  </div>
                ) : null}

                {title !== "Self" ? (
                  <div>
                    <dt>Date of Birth</dt>
                    <dd>{displayDate(card?.dob)}</dd>
                  </div>
                ) : null}

                {title === "Child" ? (
                  <div>
                    <dt>Gender</dt>
                    <dd>{displayGender(card?.gender)}</dd>
                  </div>
                ) : null}

                <div>
                  <dt>Insurance Number</dt>
                  <dd>{card?.insuranceNumber || "-"}</dd>
                </div>

                <div>
                  <dt>Valid From</dt>
                  <dd>{displayDate(card?.insuranceFrom)}</dd>
                </div>

                <div>
                  <dt>Valid Till</dt>
                  <dd>{displayDate(card?.insuranceTo)}</dd>
                </div>

                <div>
                  <dt>Uploaded Insurance</dt>
                  <dd>
                    {card?.insuranceSoftCopy ? (
                      <button
                        type="button"
                        className={styles.viewButton}
                        onClick={() => void openDocument(card.insuranceSoftCopy)}
                        disabled={loadingDocument === card.insuranceSoftCopy}
                      >
                        <Eye size={15} />
                        {loadingDocument === card.insuranceSoftCopy ? "Loading..." : "View"}
                      </button>
                    ) : (
                      "-"
                    )}
                  </dd>
                </div>
              </dl>
            </section>
       
    ))}
</div>
  
      <button
        id="open-profile-insurance-document-view"
        data-bs-toggle="modal"
        data-bs-target="#staticBackdropDocumentViewer"
        style={{ display: "none" }}
      ></button>

      {viewerUrl ? (
        <DocumentViewer
          url={viewerUrl}
          contentType={viewerType}
          fileName={viewerName}
          onClose={() => {
            setViewerUrl((current) => {
              if (current) URL.revokeObjectURL(current);
              return null;
            });
          }}
        />
      ) : null}
    </div>
  );
};

export default ProfileInsuranceDetails;
